import { useEffect, useRef, useState } from 'react';
import { GameFrame, useGame, type GameProps } from '../kit/shell';
import { loadStudioType, studioConfig, type StudioType } from '../../studio/types';

const THEME_VARS = ['--bg', '--surface', '--surface-soft', '--ink', '--ink-soft', '--primary', '--primary-ink', '--border',
  '--border-strong', '--success', '--success-bg', '--focus', '--radius', '--radius-lg', '--tile-radius', '--font-size-base', '--line'];
const MAX_TRIALS_PER_MINUTE = 120;

function themeOf(): Record<string, string> {
  const cs = getComputedStyle(document.documentElement);
  const out: Record<string, string> = {};
  for (const v of THEME_VARS) {
    const val = cs.getPropertyValue(v).trim();
    if (val) out[v] = val;
  }
  out['--font'] = getComputedStyle(document.body).fontFamily;
  return out;
}

export default function StudioGame(props: GameProps) {
  const game = useGame(props);
  const typeId = game.str('studioType', '');
  const [type, setType] = useState<StudioType | null | undefined>(undefined);
  const [height, setHeight] = useState(360);
  const [failed, setFailed] = useState<string | null>(null);
  const [lastTrial, setLastTrial] = useState<string | null>(null);
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const live = useRef(game);
  live.current = game;

  useEffect(() => {
    let cancelled = false;
    if (!typeId) { setType(null); return; }
    void loadStudioType(typeId).then((t) => { if (!cancelled) setType(t); });
    return () => { cancelled = true; };
  }, [typeId]);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || !type || !game.config || game.onBreak) return;
    const issued = new Set<string>();
    const stamps: number[] = [];
    const reply = (reqId: unknown, value: unknown, error?: string) =>
      frame.contentWindow?.postMessage({ type: 'result', reqId, value, error }, '*');

    const onMessage = async (e: MessageEvent) => {
      if (e.source !== frame.contentWindow) return;
      const m = e.data as Record<string, unknown> | null;
      if (!m || typeof m !== 'object' || typeof m.type !== 'string') return;
      const g = live.current;
      if (m.type === 'ready') {
        frame.contentWindow?.postMessage({
          type: 'init',
          code: type.code,
          config: studioConfig(type.manifest, g.config ?? {}),
          settings: { reduceMotion: g.settings.reduceMotion, errorless: g.errorless, textPrompt: g.textPrompt, transitionMs: g.transitionMs },
          theme: themeOf(),
        }, '*');
      } else if (m.type === 'register') {
        const now = Date.now();
        while (stamps.length && now - stamps[0] > 60_000) stamps.shift();
        if (stamps.length >= MAX_TRIALS_PER_MINUTE) { reply(m.reqId, null, 'too many rounds'); return; }
        stamps.push(now);
        const presented = m.presented && typeof m.presented === 'object' ? m.presented as Record<string, unknown> : {};
        const answer = m.answer && typeof m.answer === 'object' ? m.answer as Record<string, unknown> : null;
        if (JSON.stringify(presented).length > 4000 || JSON.stringify(answer ?? {}).length > 1000) { reply(m.reqId, null, 'round too large'); return; }
        const id = await g.register({ ...presented, studioType: type.manifest.id, studioVersion: type.manifest.version }, answer);
        if (id) { issued.add(id); setLastTrial(id); }
        reply(m.reqId, id, id ? undefined : 'could not start the round');
      } else if (m.type === 'respond') {
        if (typeof m.trialId !== 'string' || !issued.has(m.trialId)) { reply(m.reqId, null, 'unknown round'); return; }
        issued.delete(m.trialId);
        const v = await g.respond(m.trialId, String(m.response ?? '').slice(0, 200));
        reply(m.reqId, v ? { correct: v.correct } : null, v ? undefined : 'could not record the answer');
      } else if (m.type === 'rest') {
        if (typeof m.trialId === 'string' && issued.has(m.trialId)) await g.takeBreak(m.trialId);
        reply(m.reqId, true);
      } else if (m.type === 'say') {
        if (typeof m.text === 'string') g.say(m.text.slice(0, 500));
      } else if (m.type === 'resize') {
        const h = Number(m.height);
        if (Number.isFinite(h)) setHeight(Math.max(120, Math.min(4000, Math.round(h))));
      } else if (m.type === 'error') {
        setFailed(typeof m.message === 'string' ? m.message : 'error');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [type, game.config, game.onBreak]);

  const empty = type === null || (type !== undefined && !!game.config && type.manifest.content.some((f) => {
    const v = (studioConfig(type.manifest, game.config ?? {}) as Record<string, unknown>)[f.id];
    return f.type === 'words' && Array.isArray(v) && v.length < (f.min ?? 1);
  }));

  return (
    <GameFrame
      game={game}
      empty={empty}
      emptyText={type === null ? 'This program uses a Studio game that is not available.' : 'Edit it to add what the learner practises.'}
      onResume={() => setFailed(null)}
      breakTrial={lastTrial}
    >
      {type && (
        <div className="g-board g-studio">
          {failed && <div className="error" role="alert">This game stopped working. Try reloading the page.</div>}
          <iframe
            key={game.onBreak ? 'break' : 'play'}
            ref={frameRef}
            className="g-studio-frame"
            title={type.manifest.name}
            src="/studio/runtime.html"
            sandbox="allow-scripts"
            referrerPolicy="no-referrer"
            style={{ height }}
          />
        </div>
      )}
    </GameFrame>
  );
}
